// The TCS app's one door into EVSiQ: its manual-log function (write path A, Ben 2026-10-27).
// The function checks the caller with the TCS database itself (their own sign-in), so this sends only
// the TCS token — no EVSiQ key, and only the headers the function's CORS allows (PLAYBOOK §6).
import { getSupabaseToken } from '../../../shared/supabase';

/** Repeated in scripts/lib/evsiq.mjs; evsiq-manual-log.test.ts keeps the two equal. */
export const EVSIQ_MANUAL_LOG_URL = 'https://idkyvmfaqfrmkprykonq.supabase.co/functions/v1/manual-log';

export interface EvsiqCleaner { uuid: string; name: string; sub_name: string | null }
export interface EvsiqOptions {
  on_evsiq: boolean;
  facility?: { uuid: string; name: string };
  rooms?: { id: string; name: string }[];
  cleaners?: EvsiqCleaner[];
}
export interface ManualLogInput {
  facility_id: string;
  room_id: string;
  cleaned_by: string;
  /** When it was cleaned, as an ISO instant. Never in the future. */
  logged_at: string;
  notes: string | null;
  /** Optional — a photo is never required to log a clean (Ben, 2026-10-26). */
  photos: { base64: string; mime: string }[];
}
export interface ManualLogSaved {
  id: string; logged_at: string; received_at: string; photos: number;
  cleaner: string; sub_name: string | null; room: string;
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const jwt = getSupabaseToken();
  if (!jwt) throw new Error('You need to be signed in to the TCS app.');
  const res = await fetch(EVSIQ_MANUAL_LOG_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `EVSiQ answered ${res.status}`);
  return data;
}

export const fetchEvsiqOptions = (facilityId: string) =>
  call<EvsiqOptions>({ action: 'options', facility_id: facilityId });

export const saveManualLog = (input: ManualLogInput) =>
  call<ManualLogSaved>({ action: 'save', ...input });

/** A file as base64 for the function (the data-URL prefix removed). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error ?? new Error('The photo could not be read.'));
    r.readAsDataURL(file);
  });
}

/** "Griselda (Shiny Floors Cleaning Service LLC)" — a cleaner is named with their sub. */
export const cleanerLabel = (c: EvsiqCleaner) => (c.sub_name ? `${c.name} (${c.sub_name})` : c.name);

/** A datetime-local value ("2026-10-26T22:50") read as this device's clock → ISO; null if blank or bad. */
export function localInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
